import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import api, { apiError } from "../lib/api";

const CONTENT = {
  jewellery: {
    title: "Zelvoria Jewellery",
    short: "Jewellery",
    tagline: "Elegant pieces for life's special moments.",
    image: "/images/jewellery.jpg",
    alt: "Fine gold Zelvoria jewellery resting on cream marble with soft shadows",
  },
  fashion: {
    title: "Zelvoria Fashion",
    short: "Fashion",
    tagline: "Timeless styles for the modern you.",
    image: "/images/fashion.jpg",
    alt: "Ivory and beige silk garments in a sophisticated Zelvoria boutique",
  },
};

export default function ComingSoon({ kind }) {
  const c = CONTENT[kind];
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", { email, collection: kind });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid={`${kind}-page`}>
      <Seo
        title={`${c.title} — Coming Soon`}
        siteName="ZELVORIA"
        description={`${c.tagline} Join the waitlist.`}
        image={c.image}
      />
      <div className="mx-auto grid max-w-7xl items-center md:grid-cols-2 md:gap-16 md:px-10 md:py-20">
        <Reveal className="relative h-[52vh] overflow-hidden md:h-[76vh]">
          <img src={c.image} alt={c.alt} className="h-full w-full object-cover" />
        </Reveal>
        <div className="px-6 py-16 text-center md:px-0 md:text-left">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Coming Soon</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-4 font-serif text-4xl font-light text-ink md:text-6xl">{c.title}</h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-5 font-serif text-xl italic text-taupe md:text-2xl">{c.tagline}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mx-auto my-8 h-px w-20 bg-gold md:mx-0" />
            <p className="mx-auto max-w-md text-sm font-light leading-relaxed text-taupe md:mx-0">
              The world of Zelvoria is unfolding. Leave your email and be the first to step into {c.short} when it
              arrives — before anyone else.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            {done ? (
              <p className="mt-10 font-serif text-2xl italic text-gold" data-testid="waitlist-success">
                You're on the list. We'll write soon.
              </p>
            ) : (
              <form
                onSubmit={submit}
                data-testid="waitlist-form"
                className="mx-auto mt-10 flex max-w-md border border-cocoa/25 transition-colors focus-within:border-gold md:mx-0"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  data-testid="waitlist-email-input"
                  className="w-full bg-transparent px-5 py-4 text-sm font-light outline-none placeholder:text-taupe/50"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit-button"
                  className="flex shrink-0 items-center gap-2 bg-cocoa px-6 text-[10px] uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold disabled:opacity-60"
                >
                  Join the Waitlist <ArrowRight size={13} />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </div>
  );
}
